/**
 * ExpiredVisitorsView
 *
 * Two tabs:
 *  - "Visitantes" — compact row list of expired/exited visitors with bulk revoke tools
 *  - "Agendamentos" — deferred device-removal queue: person + device name + scheduled time
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  Clock,
  Users,
  CheckSquare,
  Square,
  User,
  Search,
  Wifi,
  WifiOff,
  AlertCircle,
  CheckCircle2,
  ShieldOff,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Timer,
  Cpu,
  Play,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useExpiredVisitors, ExpiredVisitor } from '../../../hooks/useExpiredVisitors';
import { DeviceRemovalProgress } from './DeviceRemovalProgress';
import {
  expiredVisitorDeviceService,
  DevicePresenceSummary,
} from '../../../services/expiredVisitorDeviceService';
import { isElectron } from '../../../utils/platform';
import { fetchWithAuth, getApiBaseUrl, makeAbsolutePhotoUrl } from '../../../services/api';
import { removePersonFromDevices } from '../../../utils/deviceRevoke';
import { DeviceInfo } from '../../../services/deviceCommunication';
import toast from 'react-hot-toast';
import { useConfirm } from '../../ui/ConfirmDialog';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';

// ─── Deferred Removal Queue types & hook ─────────────────────────────────────

interface DeferredRemovalQueueItem {
  id: string;
  person_id: string;
  person_name: string;
  person_photo: string;
  device_id: string;
  device_name: string;
  zone_name: string;
  scheduled_at: string;
  executed_at?: string;
  /** pending | processing | confirmed | failed */
  removal_status: string;
  created_at: string;
}

function useDeferredRemovalQueue(customerId: string) {
  const [items, setItems] = useState<DeferredRemovalQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const resp = await fetchWithAuth(
        `${getApiBaseUrl()}/api/v1/customers/${customerId}/deferred-removals/queue`
      );
      if (resp.ok) {
        const data = await resp.json();
        setItems(data.items || []);
      }
    } catch {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    refresh();
    intervalRef.current = setInterval(refresh, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  return { items, isLoading, refresh };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatRelativeDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `Há ${diffDays}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/** Returns a human-readable countdown/status for a scheduled removal */
function formatScheduled(
  scheduledAt: string,
  executedAt: string | undefined,
  removalStatus: string,
): { label: string; color: string; spin?: boolean } {
  if (removalStatus === 'confirmed') {
    const t = new Date(executedAt ?? scheduledAt);
    return {
      label: `Removido às ${t.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
      color: 'text-green-400',
    };
  }
  if (removalStatus === 'failed') {
    return { label: 'Falha — aguard. nova tentativa', color: 'text-red-400' };
  }
  if (removalStatus === 'processing') {
    return { label: 'Removendo…', color: 'text-amber-400', spin: true };
  }
  // pending: show countdown
  const diffMs = new Date(scheduledAt).getTime() - Date.now();
  if (diffMs > 0) {
    const diffMin = Math.ceil(diffMs / 60000);
    if (diffMin < 60) return { label: `em ${diffMin}min`, color: 'text-blue-400' };
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return { label: `em ${h}h${m > 0 ? `${m}m` : ''}`, color: 'text-blue-400' };
  }
  return { label: 'Aguardando…', color: 'text-amber-400' };
}

// ─── VisitorRow ──────────────────────────────────────────────────────────────

interface VisitorRowProps {
  visitor: ExpiredVisitor;
  isSelected: boolean;
  presenceDeviceCount: number | null;
  onToggleSelect: (id: string) => void;
  onPersonClick: (personId: string) => void;
}

const VisitorRow: React.FC<VisitorRowProps> = ({
  visitor,
  isSelected,
  presenceDeviceCount,
  onToggleSelect,
  onPersonClick,
}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors ${
        isSelected ? 'bg-gray-900/60' : ''
      }`}
    >
      <button
        onClick={() => onToggleSelect(visitor.id)}
        className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
      >
        {isSelected ? (
          <CheckSquare className="w-4 h-4 text-red-500" />
        ) : (
          <Square className="w-4 h-4" />
        )}
      </button>

      <div
        className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center cursor-pointer"
        onClick={() => onPersonClick(visitor.personId)}
      >
        {visitor.photo && !imgError ? (
          <img
            src={getAbsoluteImageUrl(visitor.photo)}
            alt={visitor.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <User className="w-4 h-4 text-gray-500" />
        )}
      </div>

      <button
        onClick={() => onPersonClick(visitor.personId)}
        className="flex-1 min-w-0 text-left"
      >
        <span className="text-sm font-medium text-gray-200 truncate block hover:text-white transition-colors">
          {visitor.name}
        </span>
      </button>

      {presenceDeviceCount !== null && (
        <span
          className={`flex-shrink-0 flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
            presenceDeviceCount > 0
              ? 'text-amber-400 bg-amber-950/40'
              : 'text-gray-600 bg-gray-900'
          }`}
          title={
            presenceDeviceCount > 0
              ? `Encontrado em ${presenceDeviceCount} dispositivo(s)`
              : 'Não encontrado em nenhum dispositivo'
          }
        >
          {presenceDeviceCount > 0 ? (
            <><Wifi className="w-3 h-3" />{presenceDeviceCount}</>
          ) : (
            <><WifiOff className="w-3 h-3" />0</>
          )}
        </span>
      )}

      <span
        className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
          visitor.statusType === 'expired'
            ? 'bg-red-950 text-red-400'
            : 'bg-gray-800 text-gray-400'
        }`}
      >
        {visitor.statusType === 'expired' ? 'Expirado' : 'Saiu'}
      </span>

      <span className="flex-shrink-0 w-14 text-xs text-gray-500 text-right">
        {formatRelativeDate(visitor.expiredAt)}
      </span>
    </div>
  );
};

// ─── PresencePanel ───────────────────────────────────────────────────────────

const PresencePanel: React.FC<{
  deviceSummary: DevicePresenceSummary[];
  totalFound: number;
  onRevokeConfirmed: () => void;
  isRemoving: boolean;
}> = ({ deviceSummary, totalFound, onRevokeConfirmed, isRemoving }) => (
  <div className="mx-4 mb-3 rounded-lg border border-amber-900/50 bg-amber-950/20 p-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-amber-400 mb-2">
          {totalFound > 0
            ? `${totalFound} visitante${totalFound !== 1 ? 's' : ''} encontrado${totalFound !== 1 ? 's' : ''} nos dispositivos`
            : 'Nenhum visitante encontrado nos dispositivos'}
        </p>
        <div className="flex flex-wrap gap-2">
          {deviceSummary.map(d => (
            <div
              key={d.deviceId}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                d.status === 'error'
                  ? 'bg-red-950/40 text-red-400'
                  : d.foundCount > 0
                  ? 'bg-amber-950/40 text-amber-300'
                  : 'bg-gray-900 text-gray-500'
              }`}
            >
              {d.status === 'error' ? (
                <AlertCircle className="w-3 h-3" />
              ) : d.foundCount > 0 ? (
                <Wifi className="w-3 h-3" />
              ) : (
                <WifiOff className="w-3 h-3" />
              )}
              <span className="font-mono text-[10px]">{d.deviceId}</span>
              {d.status !== 'error' && (
                <span className="text-gray-500">({d.foundCount})</span>
              )}
              {d.status === 'error' && <span className="text-red-500">erro</span>}
              <span className="text-gray-700 text-[10px]">
                {d.source === 'local' ? 'local' : d.source === 'public' ? 'pub' : 'cloud'}
              </span>
            </div>
          ))}
        </div>
      </div>
      {totalFound > 0 && (
        <button
          onClick={onRevokeConfirmed}
          disabled={isRemoving}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700
                     disabled:bg-gray-700 disabled:cursor-not-allowed
                     text-white text-xs font-semibold rounded transition-colors whitespace-nowrap"
        >
          <ShieldOff className="w-3.5 h-3.5" />
          Revogar confirmados ({totalFound})
        </button>
      )}
    </div>
  </div>
);

// ─── DeferredQueueRow ─────────────────────────────────────────────────────────

/** Countdown to GC retry: executed_at + 10 min */
function useGcCountdown(executedAt: string | undefined): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!executedAt) return;
    const retryAt = new Date(executedAt).getTime() + 10 * 60 * 1000;
    const tick = () => {
      const ms = retryAt - Date.now();
      if (ms <= 0) { setLabel(null); return; }
      const s = Math.ceil(ms / 1000);
      if (s < 60) setLabel(`${s}s`);
      else setLabel(`${Math.ceil(s / 60)}min`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [executedAt]);
  return label;
}

const DeferredQueueRow: React.FC<{
  item: DeferredRemovalQueueItem;
  customerId: string;
  onPersonClick: (personId: string) => void;
  onTriggered: () => void;
}> = ({ item, customerId, onPersonClick, onTriggered }) => {
  const [imgError, setImgError] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const { label, color, spin } = formatScheduled(item.scheduled_at, item.executed_at, item.removal_status);
  const gcCountdown = useGcCountdown(item.removal_status === 'failed' ? item.executed_at : undefined);
  const photoUrl = item.person_photo ? makeAbsolutePhotoUrl(item.person_photo) : null;
  const canTrigger = item.removal_status !== 'confirmed' && item.removal_status !== 'processing' && !triggering;

  const StatusIcon = item.removal_status === 'confirmed'
    ? CheckCircle2
    : item.removal_status === 'failed'
    ? AlertCircle
    : item.removal_status === 'processing'
    ? RefreshCw
    : Timer;

  const handleTrigger = async () => {
    if (!canTrigger) return;
    setTriggering(true);
    const electronEnv = isElectron();
    let localDone = false;
    try {
      if (electronEnv) {
        // Try hybrid local removal first — cloud iot-connect can't reach local-network devices
        try {
          const deviceResp = await fetchWithAuth(`${getApiBaseUrl()}/api/v1/iot-devices/${item.device_id}`);
          if (deviceResp.ok) {
            const d = await deviceResp.json();
            const mfr = (d.manufacturer || '').toLowerCase();
            const vendor: DeviceInfo['vendor'] =
              mfr === 'controlid' ? 'controlid' :
              mfr === 'hikvision' ? 'hikvision' : 'hikvision';
            const device: DeviceInfo = {
              id: d.id,
              vendor,
              ip_address: d.ip_address,
              local_ip_address: d.local_ip_address,
              port: d.port,
              username: d.username || 'admin',
              password: d.password || '',
            };
            await removePersonFromDevices(customerId, [device], item.person_id, item.person_name, 2);
          }
        } catch { /* best-effort */ }
        localDone = true; // Electron is the only path that can reach local-network devices
      }

      const resp = await fetchWithAuth(
        `${getApiBaseUrl()}/api/v1/customers/${customerId}/deferred-removals/${item.id}/trigger`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ local_removal_done: localDone }),
        }
      );
      if (resp.ok) {
        toast.success(localDone ? 'Removido do dispositivo' : 'Remoção iniciada');
        onTriggered();
      } else {
        toast.error('Falha ao iniciar remoção');
      }
    } catch {
      toast.error('Falha ao iniciar remoção');
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors">
      {/* Photo */}
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center cursor-pointer"
        onClick={() => onPersonClick(item.person_id)}
      >
        {photoUrl && !imgError ? (
          <img
            src={photoUrl}
            alt={item.person_name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <User className="w-3.5 h-3.5 text-gray-500" />
        )}
      </div>

      {/* Person name */}
      <button
        onClick={() => onPersonClick(item.person_id)}
        className="w-28 flex-shrink-0 text-left"
      >
        <span className="text-sm font-medium text-gray-200 truncate block hover:text-white transition-colors">
          {item.person_name}
        </span>
      </button>

      {/* Device + zone */}
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <Cpu className="w-3 h-3 text-gray-600 flex-shrink-0" />
        <span className="text-xs text-gray-400 truncate">
          {item.device_name}
          {item.zone_name && item.zone_name !== item.device_name && (
            <span className="text-gray-600 ml-1">· {item.zone_name}</span>
          )}
        </span>
      </div>

      {/* Status + GC countdown */}
      <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
        <span className={`flex items-center gap-1 text-xs font-medium ${color}`}>
          <StatusIcon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
          {label}
        </span>
        {gcCountdown && (
          <span className="text-[10px] text-gray-600">nova tent. em {gcCountdown}</span>
        )}
      </div>

      {/* Manual trigger button */}
      {item.removal_status !== 'confirmed' && (
        <button
          onClick={handleTrigger}
          disabled={!canTrigger}
          title="Executar remoção agora"
          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs font-medium
                     bg-blue-900/50 hover:bg-blue-800/70 text-blue-300 hover:text-white
                     border border-blue-800/50 rounded transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {triggering ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : (
            <Play className="w-3 h-3" />
          )}
        </button>
      )}
    </div>
  );
};

// ─── Main view ───────────────────────────────────────────────────────────────

interface ExpiredVisitorsViewProps {
  customerId: string;
  onBack: () => void;
  onPersonClick: (personId: string) => void;
}

export const ExpiredVisitorsView: React.FC<ExpiredVisitorsViewProps> = ({
  customerId,
  onBack,
  onPersonClick,
}) => {
  const confirm = useConfirm();
  const { visitors, isLoading, error, refresh } = useExpiredVisitors(customerId);
  const { items: queueItems, isLoading: queueLoading, refresh: refreshQueue } = useDeferredRemovalQueue(customerId);

  type ActiveTab = 'visitors' | 'queue';
  const [activeTab, setActiveTab] = useState<ActiveTab>('visitors');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isRemoving, setIsRemoving] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [removalProgress, setRemovalProgress] = useState({ current: 0, total: 0, name: '' });

  // ── Date filter & pagination ─────────────────────────────────────────────
  type DateFilter = 'today' | 'yesterday' | '7d' | '30d' | 'custom';
  const [dateFilter, setDateFilter] = useState<DateFilter>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const filteredVisitors = useMemo(() => {
    const now = new Date();
    const bod = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let start: Date;
    let end: Date = now;
    if (dateFilter === 'today') {
      start = bod(now);
    } else if (dateFilter === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      start = bod(y); end = bod(now);
    } else if (dateFilter === '7d') {
      start = new Date(now.getTime() - 7 * 86400000);
    } else if (dateFilter === '30d') {
      start = new Date(now.getTime() - 30 * 86400000);
    } else {
      start = customStart ? new Date(customStart) : new Date(0);
      end = customEnd ? new Date(customEnd + 'T23:59:59') : now;
    }
    return visitors.filter(v => { const d = new Date(v.expiredAt); return d >= start && d <= end; });
  }, [visitors, dateFilter, customStart, customEnd]);

  const uniqueVisitorCount = useMemo(() => new Set(filteredVisitors.map(v => v.personId)).size, [filteredVisitors]);
  const filteredExpiredCount = useMemo(() => filteredVisitors.filter(v => v.statusType === 'expired').length, [filteredVisitors]);
  const filteredExitedCount  = useMemo(() => filteredVisitors.filter(v => v.statusType === 'exited').length,  [filteredVisitors]);
  const totalPages = Math.max(1, Math.ceil(filteredVisitors.length / PAGE_SIZE));
  const paginatedVisitors = filteredVisitors.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [dateFilter, customStart, customEnd]);

  // Items that haven't been confirmed yet (pending/processing/failed)
  const pendingQueueItems = useMemo(
    () => queueItems.filter(i => i.removal_status !== 'confirmed'),
    [queueItems]
  );

  // Confirmed items deduplicated by person_id — keep only the most recent per person
  // (multiple checkout cycles create multiple rows for the same person)
  const recentlyRemovedItems = useMemo(() => {
    const confirmed = queueItems.filter(i => i.removal_status === 'confirmed');
    const byPerson = new Map<string, DeferredRemovalQueueItem>();
    for (const item of confirmed) {
      const existing = byPerson.get(item.person_id);
      if (!existing || new Date(item.scheduled_at) > new Date(existing.scheduled_at)) {
        byPerson.set(item.person_id, item);
      }
    }
    return Array.from(byPerson.values()).sort(
      (a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
    );
  }, [queueItems]);

  // ── Presence check ────────────────────────────────────────────────────────
  const [presenceState, setPresenceState] = useState<'idle' | 'checking' | 'done'>('idle');
  const [presenceProgress, setPresenceProgress] = useState({ checked: 0, total: 0, deviceId: '' });
  const [presenceResult, setPresenceResult] = useState<{
    visitorOnDevices: Map<string, string[]>;
    deviceSummary: DevicePresenceSummary[];
  } | null>(null);

  const totalFoundOnDevices = useMemo(
    () => presenceResult ? presenceResult.visitorOnDevices.size : 0,
    [presenceResult]
  );

  // ── Selection ─────────────────────────────────────────────────────────────

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(
      selectedIds.size === filteredVisitors.length ? new Set() : new Set(filteredVisitors.map(v => v.id))
    );
  }, [filteredVisitors, selectedIds.size]);

  // ── Removal helpers ───────────────────────────────────────────────────────

  const runRemoval = useCallback(
    async (toRemove: ExpiredVisitor[]) => {
      if (toRemove.length === 0) return;

      setIsRemoving(true);
      setShowProgress(true);
      setRemovalProgress({ current: 0, total: toRemove.length, name: '' });

      try {
        const results = await expiredVisitorDeviceService.removeBatchFromDevices(
          customerId,
          toRemove,
          (current, total, name) => setRemovalProgress({ current, total, name })
        );

        let ok = 0; let fail = 0;
        results.forEach(deviceResults =>
          deviceResults.forEach(r => (r.success ? ok++ : fail++))
        );

        if (fail === 0) {
          toast.success(`${toRemove.length} visitante${toRemove.length !== 1 ? 's' : ''} revogado${toRemove.length !== 1 ? 's' : ''}`);
        } else {
          toast.error(`Parcial: ${ok} OK, ${fail} falhou`);
        }

        setSelectedIds(new Set());
        setPresenceResult(null);
        setPresenceState('idle');
        await refresh();
      } catch (err) {
        toast.error('Falha ao revogar visitantes');
        console.error('Batch removal error:', err);
      } finally {
        setIsRemoving(false);
        setTimeout(() => setShowProgress(false), 2000);
      }
    },
    [customerId, refresh]
  );

  const handleRevogarTodos = useCallback(async () => {
    if (filteredVisitors.length === 0) return;
    const confirmed = await confirm({
      message: `Revogar TODOS os ${filteredVisitors.length} visitantes (período selecionado) de todos os dispositivos físicos?\n\nEsta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Revogar',
    });
    if (!confirmed) return;
    await runRemoval(filteredVisitors);
  }, [filteredVisitors, runRemoval, confirm]);

  const handleRevogarConfirmados = useCallback(async () => {
    if (!presenceResult) return;
    const confirmedIds = new Set(presenceResult.visitorOnDevices.keys());
    const toRemove = visitors.filter(v => confirmedIds.has(v.id));
    if (toRemove.length === 0) return;
    const confirmed = await confirm({
      message: `Revogar ${toRemove.length} visitante${toRemove.length !== 1 ? 's' : ''} confirmado${toRemove.length !== 1 ? 's' : ''} nos dispositivos?\n\nEsta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Revogar',
    });
    if (!confirmed) return;
    await runRemoval(toRemove);
  }, [presenceResult, visitors, runRemoval, confirm]);

  const handleVerificarDispositivos = useCallback(async () => {
    setPresenceState('checking');
    setPresenceProgress({ checked: 0, total: 0, deviceId: '' });
    setPresenceResult(null);

    try {
      const result = await expiredVisitorDeviceService.checkPresenceOnDevices(
        customerId,
        visitors,
        (checked, total, deviceId) => setPresenceProgress({ checked, total, deviceId })
      );
      setPresenceResult(result);
      setPresenceState('done');

      const found = result.visitorOnDevices.size;
      if (found > 0) {
        toast.success(`${found} visitante${found !== 1 ? 's' : ''} encontrado${found !== 1 ? 's' : ''} nos dispositivos`);
      } else {
        toast(`Nenhum visitante encontrado nos dispositivos`);
      }
    } catch (err) {
      toast.error('Falha ao verificar dispositivos');
      console.error('Presence check error:', err);
      setPresenceState('idle');
    }
  }, [customerId, visitors]);

  const handleRefresh = async () => {
    setPresenceResult(null);
    setPresenceState('idle');
    await Promise.all([refresh(), refreshQueue()]);
    toast.success('Atualizado');
  };

  const handleRemoveSelected = useCallback(async () => {
    const toRemove = visitors.filter(v => selectedIds.has(v.id));
    if (toRemove.length === 0) { toast.error('Nenhum visitante selecionado'); return; }
    const confirmed = await confirm({
      message: `Revogar ${toRemove.length} visitante${toRemove.length !== 1 ? 's' : ''} selecionado${toRemove.length !== 1 ? 's' : ''} de todos os dispositivos?\n\nEsta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Revogar',
    });
    if (!confirmed) return;
    await runRemoval(toRemove);
  }, [visitors, selectedIds, runRemoval, confirm]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-gray-950">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800 flex-wrap">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-300 hover:text-white
                     bg-gray-900 hover:bg-gray-800 rounded transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Clock className="w-5 h-5 text-red-500" />
          <h1 className="text-base font-semibold text-white">Visitantes Expirados</h1>
        </div>

        {visitors.length > 0 && (
          <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-900 rounded-full text-xs text-gray-400 flex-shrink-0">
            <Users className="w-3.5 h-3.5" />
            {filteredExpiredCount > 0 && <span className="text-red-400">{filteredExpiredCount} exp.</span>}
            {filteredExpiredCount > 0 && filteredExitedCount > 0 && <span className="text-gray-600">·</span>}
            {filteredExitedCount > 0 && <span>{filteredExitedCount} saíu{filteredExitedCount !== 1 ? 'ram' : ''}</span>}
          </span>
        )}

        <div className="flex-1" />

        {/* Action buttons — only shown on visitors tab */}
        {activeTab === 'visitors' && (
          <div className="flex items-center gap-2 flex-wrap">
            {isElectron() && visitors.length > 0 && (
              <button
                onClick={handleVerificarDispositivos}
                disabled={presenceState === 'checking' || isRemoving || isLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           text-blue-300 hover:text-blue-100 bg-blue-950/50 hover:bg-blue-950
                           border border-blue-900/50 rounded transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {presenceState === 'checking' ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    {presenceProgress.total > 0
                      ? `Verificando ${presenceProgress.checked}/${presenceProgress.total}…`
                      : 'Verificando…'}
                  </>
                ) : presenceState === 'done' ? (
                  <><CheckCircle2 className="w-3.5 h-3.5 text-green-400" />Reverificar</>
                ) : (
                  <><Search className="w-3.5 h-3.5" />Verificar Dispositivos</>
                )}
              </button>
            )}

            {visitors.length > 0 && (
              <button
                onClick={handleRevogarTodos}
                disabled={isRemoving || isLoading || presenceState === 'checking'}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                           bg-red-700 hover:bg-red-600 text-white rounded transition-colors
                           disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                <ShieldOff className="w-3.5 h-3.5" />
                Revogar Todos ({visitors.length})
              </button>
            )}
          </div>
        )}

        <button
          onClick={handleRefresh}
          disabled={isLoading || queueLoading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-white
                     bg-gray-900 hover:bg-gray-800 rounded transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${(isLoading || queueLoading) ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex border-b border-gray-800 flex-shrink-0">
        <button
          onClick={() => setActiveTab('visitors')}
          className={cn(
            'px-5 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'visitors'
              ? 'border-red-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          )}
        >
          Visitantes
        </button>
        <button
          onClick={() => setActiveTab('queue')}
          className={cn(
            'px-5 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
            activeTab === 'queue'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          )}
        >
          Agendamentos
          {pendingQueueItems.length > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-blue-600 text-white rounded-full leading-none">
              {pendingQueueItems.length}
            </span>
          )}
        </button>
      </div>

      {/* ── Tab: Visitantes ── */}
      {activeTab === 'visitors' && (
        <>
          {/* Bulk Actions Bar */}
          {visitors.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-gray-900/20 border-b border-gray-800/60 flex-shrink-0">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
              >
                {selectedIds.size === visitors.length ? (
                  <CheckSquare className="w-4 h-4 text-red-500" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedIds.size === visitors.length ? 'Desmarcar todos' : 'Selecionar todos'}
                {selectedIds.size > 0 && selectedIds.size !== visitors.length && (
                  <span className="text-gray-600 ml-1">
                    ({selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''})
                  </span>
                )}
              </button>

              {selectedIds.size > 0 && (
                <button
                  onClick={handleRemoveSelected}
                  disabled={isRemoving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-700 hover:bg-red-600
                             disabled:bg-gray-700 disabled:cursor-not-allowed
                             text-white text-xs font-medium rounded transition-colors"
                >
                  <ShieldOff className="w-3.5 h-3.5" />
                  Revogar selecionados ({selectedIds.size})
                </button>
              )}
            </div>
          )}

          <div className="flex flex-1 min-h-0">
            {/* Visitor list */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {filteredVisitors.length > 0 && !isLoading && (
                <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-800/40 bg-gray-900/10 flex-shrink-0">
                  <div className="w-4 flex-shrink-0" />
                  <div className="w-9 flex-shrink-0" />
                  <span className="flex-1 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Nome</span>
                  {presenceState === 'done' && (
                    <span className="flex-shrink-0 w-12 text-[10px] font-medium text-gray-600 uppercase tracking-wider text-right">Dispos.</span>
                  )}
                  <span className="flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Status</span>
                  <span className="w-14 flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider text-right">Data</span>
                </div>
              )}
              <div className="flex-1 overflow-auto">
                {isLoading && (
                  <div className="flex items-center justify-center h-48">
                    <RefreshCw className="w-6 h-6 text-gray-600 animate-spin" />
                  </div>
                )}
                {error && !isLoading && (
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <p className="text-red-400 text-sm">Falha ao carregar visitantes</p>
                      <button onClick={handleRefresh} className="mt-2 text-xs text-gray-500 hover:text-white underline">Tentar novamente</button>
                    </div>
                  </div>
                )}
                {!isLoading && !error && filteredVisitors.length === 0 && (
                  <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                      <Clock className="w-12 h-12 text-gray-800 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Nenhum visitante no período</p>
                    </div>
                  </div>
                )}
                {!isLoading && presenceState === 'done' && presenceResult && (
                  <div className="pt-3">
                    <PresencePanel
                      deviceSummary={presenceResult.deviceSummary}
                      totalFound={totalFoundOnDevices}
                      onRevokeConfirmed={handleRevogarConfirmados}
                      isRemoving={isRemoving}
                    />
                  </div>
                )}
                {!isLoading && !error && paginatedVisitors.length > 0 && (
                  <div>
                    {paginatedVisitors.map(visitor => {
                      const presenceDeviceCount =
                        presenceState === 'done' && presenceResult
                          ? (presenceResult.visitorOnDevices.get(visitor.id)?.length ?? 0)
                          : null;
                      return (
                        <VisitorRow
                          key={visitor.id}
                          visitor={visitor}
                          isSelected={selectedIds.has(visitor.id)}
                          presenceDeviceCount={presenceDeviceCount}
                          onToggleSelect={handleToggleSelect}
                          onPersonClick={onPersonClick}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-gray-800/60 flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredVisitors.length)} de {filteredVisitors.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      className="p-1 rounded hover:bg-gray-800 text-gray-400 disabled:opacity-30 transition-colors">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-400 px-1">{page}/{totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                      className="p-1 rounded hover:bg-gray-800 text-gray-400 disabled:opacity-30 transition-colors">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right sidebar: date filter + stats */}
            <div className="w-52 flex-shrink-0 border-l border-gray-800 p-3 overflow-y-auto flex flex-col gap-4">
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Período
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(['today', 'yesterday', '7d', '30d', 'custom'] as const).map(f => (
                    <button key={f} onClick={() => setDateFilter(f)}
                      className={cn("px-2.5 py-1 text-xs font-medium rounded-full transition-colors",
                        dateFilter === f ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700"
                      )}>
                      {f === 'today' ? 'Hoje' : f === 'yesterday' ? 'Ontem' : f === '7d' ? '7 dias' : f === '30d' ? '30 dias' : 'Período'}
                    </button>
                  ))}
                </div>
                {dateFilter === 'custom' && (
                  <div className="mt-2 space-y-1.5">
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-blue-500" />
                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-blue-500" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="bg-gray-900 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 mb-1">Total de Visitas</p>
                  <p className="text-2xl font-bold text-white">{filteredVisitors.length}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 mb-1">Visitantes Únicos</p>
                  <p className="text-2xl font-bold text-blue-400">{uniqueVisitorCount}</p>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-gray-500 mb-0.5">Expirados</p>
                    <p className="text-xl font-bold text-red-400">{filteredExpiredCount}</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-gray-500 mb-0.5">Saíram</p>
                    <p className="text-xl font-bold text-gray-300">{filteredExitedCount}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Tab: Agendamentos ── */}
      {activeTab === 'queue' && (
        <div className="flex flex-1 min-h-0 flex-col">
          {/* Column header */}
          {queueItems.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-800/40 bg-gray-900/10 flex-shrink-0">
              <div className="w-8 flex-shrink-0" />
              <span className="w-32 flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Pessoa</span>
              <span className="flex-1 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Dispositivo · Zona</span>
              <span className="flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Agendado</span>
            </div>
          )}

          <div className="flex-1 overflow-auto">
            {queueLoading && queueItems.length === 0 && (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-6 h-6 text-gray-600 animate-spin" />
              </div>
            )}

            {!queueLoading && queueItems.length === 0 && (
              <div className="flex items-center justify-center h-48">
                <div className="text-center">
                  <Timer className="w-12 h-12 text-gray-800 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Nenhuma remoção agendada</p>
                  <p className="text-gray-700 text-xs mt-1">
                    Ative o atraso de remoção nas configurações do cliente
                  </p>
                </div>
              </div>
            )}

            {queueItems.length > 0 && (
              <div>
                {/* Pending section */}
                {pendingQueueItems.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 bg-blue-950/20 border-b border-blue-900/30">
                      <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">
                        Aguardando remoção — {pendingQueueItems.length}
                      </span>
                    </div>
                    {pendingQueueItems.map(item => (
                      <DeferredQueueRow
                        key={item.id}
                        item={item}
                        customerId={customerId}
                        onPersonClick={onPersonClick}
                        onTriggered={refreshQueue}
                      />
                    ))}
                  </>
                )}

                {/* Recently confirmed section — deduplicated by person */}
                {recentlyRemovedItems.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 bg-gray-900/40 border-b border-gray-800/40 mt-1">
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        Removidos recentemente
                      </span>
                    </div>
                    {recentlyRemovedItems.map(item => (
                      <DeferredQueueRow
                        key={item.id}
                        item={item}
                        customerId={customerId}
                        onPersonClick={onPersonClick}
                        onTriggered={refreshQueue}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Progress Modal ── */}
      <DeviceRemovalProgress
        isOpen={showProgress}
        currentIndex={removalProgress.current}
        totalCount={removalProgress.total}
        currentVisitorName={removalProgress.name}
        onClose={() => setShowProgress(false)}
      />
    </div>
  );
};
